import React, { useState } from "react";
import { Check, X } from "lucide-react";
import { Button } from "./button";
import { Badge } from "./badge";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

export function MultiSelect({ options, selected, onChange, placeholder = "Select items..." }) {
  const [open, setOpen] = useState(false);
  const [tempSelected, setTempSelected] = useState(selected);

  // When opening, sync temp with current selected
  const handleOpenChange = (newOpen) => {
    if (newOpen) {
      setTempSelected(selected);
    }
    setOpen(newOpen);
  };

  const toggleItem = (value) => {
    if (tempSelected.includes(value)) {
      setTempSelected(tempSelected.filter(item => item !== value));
    } else {
      setTempSelected([...tempSelected, value]);
    }
  };

  const clearAll = () => {
    setTempSelected([]);
  };

  const handleConfirm = () => {
    onChange(tempSelected);
    setOpen(false);
  };

  const handleCancel = () => {
    setTempSelected(selected); // Reset to original
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
        >
          <span className="truncate">
            {selected.length === 0
              ? placeholder
              : `${selected.length} selected`}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-[95vw] sm:w-[400px] p-0" 
        align="start"
        side="bottom"
        sideOffset={8}
        collisionPadding={20}
      >
        <div className="flex flex-col h-auto max-h-[50vh] sm:max-h-[300px]">
          {/* Header */}
          <div className="flex items-center justify-between p-2 border-b flex-shrink-0 bg-white">
            <span className="text-sm font-medium">
              {tempSelected.length} selected
            </span>
            {tempSelected.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAll}
                className="h-6 text-xs"
              >
                Clear all
              </Button>
            )}
          </div>

          {/* Options */}
          <div className="flex-1 overflow-y-auto p-2 space-y-1 min-h-0">
            {options.map((option) => {
              const isSelected = tempSelected.includes(option.value);
              return (
                <div
                  key={option.value}
                  onClick={() => toggleItem(option.value)}
                  className={`flex items-center px-2 py-1.5 cursor-pointer rounded hover:bg-slate-100 ${
                    isSelected ? "bg-slate-50" : ""
                  }`}
                >
                  <div
                    className={`w-4 h-4 border rounded mr-2 flex items-center justify-center ${
                      isSelected ? "bg-blue-600 border-blue-600" : "border-slate-300"
                    }`}
                  >
                    {isSelected && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <span className="text-sm">{option.label}</span>
                </div>
              );
            })}
          </div>

          {/* Footer with Confirm/Cancel buttons */}
          <div className="flex gap-2 p-2 border-t flex-shrink-0 bg-white sticky bottom-0">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCancel}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleConfirm}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
            >
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
